import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface MetaEngagementByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** day_no-based loader for the META Engagement "by install day" / "by active day" charts.
 *  The "active_day" toggle option is currently DISABLED in the dashboard, so only the install-day
 *  branch runs in practice; the active-day branch is kept ready for when the toggle is re-enabled.
 *
 *  Output columns (UBI-1139 + UBI-1185, real):
 *    day_no                                  X axis, aliased from install_day_no (or active_day_no).
 *    all_share (1.0), meta_share, hopa_share, core_loop_share, meta_productive_share
 *                                            presence-based daily user-type shares (fraction of that
 *                                            day's active players in each population).
 *    median_/avg_<pop>_lifetime_days_share   for pop in {all, meta, meta_prod, hopa, core_loop} —
 *                                            the per-player Lifetime Days Share (UBI-1145: per ACTIVE
 *                                            user — aggregated over active rows only, NOT densified;
 *                                            both the median and the mean are charted). The per-cohort
 *                                            densified variant is kept commented out in installQuery.
 *    lifetime_days_share_n                   active player count per day_no backing the lifetime-share
 *                                            aggregates (same population across all pops).
 *    median_/avg_daily_inspiration_gain      daily inspiration-gain delta per active user (presence-
 *                                            based over active rows; not densified — a daily value).
 *    median_/avg_meta_time_share             the three slices of the day, ALL on the ctx denominator
 *    median_/avg_shop_time_share             (ctx_total_time_daily_min, the client total-time counter):
 *    median_/avg_hopa_time_share             META = meta_share_ctx (measured decorating flows), Shop =
 *                                            shop_share_ctx (measured shop flows), HOPA = hopa_share_ctx
 *                                            = (ctx - META - Shop) / ctx, i.e. the residual. One
 *                                            denominator for all three, so per player-day they add up
 *                                            to 1 (UBI-1270). Version-gated with ctx: NULL before
 *                                            4.0.100. RAW/uncapped and never clamped.
 *    meta_/shop_/hopa_time_share_n           non-null sample size per share. They differ: Shop stays 0
 *                                            until the flow events ship in 4.1.100, and until then its
 *                                            time is counted as 0, so HOPA absorbs it.
 *    median_/avg_hopa_time_share_by_session  event-derived HOPA Daily Time Share on a DIFFERENT pair of
 *                                            clocks: adventure_time_min / project_session_time_min
 *                                            (denominator C, engaged project-session time). Independent
 *                                            cross-check of the residual above — not part of the
 *                                            three-way split.
 *    hopa_time_share_by_session_n            non-null sample size for the project-session chart.
 *    median_/avg_career_level                per-active-user career level reached (UBI-1145), same
 *                                            per-active-user / active-rows-only treatment as above.
 *    meta_time_share_n, career_level_n        non-null sample size per day_no (per split group) backing
 *                                            the corresponding time-share / career-level aggregates.
 *
 *  Lifetime Days Share is DERIVED here, not stored: player_days (UBI-1185) stores only the running
 *  counts; the share = running_<pop>_days_count / (install_day_no + 1). UBI-1145: it is now computed
 *  PER ACTIVE USER in both modes — aggregated over the active rows only (no densification), the same
 *  treatment as the time-share / career-level metrics. The earlier per-cohort densified variant
 *  (retention-style carry-forward over inactive days to the player's maturity) is retained, commented
 *  out, in installQuery for future re-enable.
 */
export class MetaEngagementByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    MetaEngagementByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'MetaEngagementByDayNoDataLoaderFactory';

    getInstance(options: MetaEngagementByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const isInstallDay =
                    queryContext.settings?.[
                        QueryContextSettingID.META_DAY_MODE
                    ] === 'install_day';

                const maxDayNo =
                    queryContext.settings?.[
                        QueryContextSettingID.RETENTION_MAX_DAY_NO
                    ] ??
                    QueryContextDefaultSettings[
                        QueryContextSettingID.RETENTION_MAX_DAY_NO
                    ]!;
                // Numeric day-no cap (null = unlimited): a WHERE bound in active mode,
                // the GENERATE_ARRAY end in the install-day densification below.
                const maxDayNoNum =
                    maxDayNo !== 'unlimited' ? Number(maxDayNo) : null;

                const { splitSelect, splitGroupBy, where, join } =
                    BiVizDashboardUsersActivity.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const tableFullPath = generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );

                // ACTIVE-DAY mode: active_day_no is contiguous (one player_days row per
                // active day, no gaps), so aggregating the active rows directly is exact.
                // Lifetime share = running_<pop>_days_count / (active_day_no + 1).
                const activeQuery = `
-- DataLoaderId: ${this.instanceId}

SELECT
  ${splitSelect}active_day_no AS day_no,
  1.0 AS all_share,
  SAFE_DIVIDE(COUNT(DISTINCT IF(meta_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS meta_share,
  SAFE_DIVIDE(COUNT(DISTINCT IF(hopa_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS hopa_share,
  SAFE_DIVIDE(COUNT(DISTINCT IF(core_loop_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS core_loop_share,
  SAFE_DIVIDE(COUNT(DISTINCT IF(meta_prod_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS meta_productive_share,
  1.0 AS median_all_lifetime_days_share,
  APPROX_QUANTILES(SAFE_DIVIDE(running_meta_days_count, active_day_no + 1), 100)[OFFSET(50)] AS median_meta_lifetime_days_share,
  APPROX_QUANTILES(SAFE_DIVIDE(running_meta_prod_days_count, active_day_no + 1), 100)[OFFSET(50)] AS median_meta_prod_lifetime_days_share,
  APPROX_QUANTILES(SAFE_DIVIDE(running_hopa_days_count, active_day_no + 1), 100)[OFFSET(50)] AS median_hopa_lifetime_days_share,
  APPROX_QUANTILES(SAFE_DIVIDE(running_core_loop_days_count, active_day_no + 1), 100)[OFFSET(50)] AS median_core_loop_lifetime_days_share,
  1.0 AS avg_all_lifetime_days_share,
  AVG(SAFE_DIVIDE(running_meta_days_count, active_day_no + 1)) AS avg_meta_lifetime_days_share,
  AVG(SAFE_DIVIDE(running_meta_prod_days_count, active_day_no + 1)) AS avg_meta_prod_lifetime_days_share,
  AVG(SAFE_DIVIDE(running_hopa_days_count, active_day_no + 1)) AS avg_hopa_lifetime_days_share,
  AVG(SAFE_DIVIDE(running_core_loop_days_count, active_day_no + 1)) AS avg_core_loop_lifetime_days_share,
  COUNT(DISTINCT player_id) AS lifetime_days_share_n,
  -- daily inspiration gain per active user (inspiration_gain_daily = daily high-water-mark delta,
  -- 0 on active days with no new gain; non-cumulative, so aggregated over active rows only).
  APPROX_QUANTILES(inspiration_gain_daily, 100)[OFFSET(50)] AS median_daily_inspiration_gain,
  AVG(inspiration_gain_daily) AS avg_daily_inspiration_gain,
  -- per-active-user time share (denominator = ctx) and career level; RAW/uncapped (may exceed 1),
  -- aggregated over active rows only (NOT densified); NULLs excluded by APPROX_QUANTILES/AVG.
  APPROX_QUANTILES(meta_share_ctx, 100)[OFFSET(50)] AS median_meta_time_share,
  AVG(meta_share_ctx) AS avg_meta_time_share,
  COUNTIF(meta_share_ctx IS NOT NULL) AS meta_time_share_n,
  -- Event-derived HOPA share: adventure time over engaged project-session time (C)
  APPROX_QUANTILES(SAFE_DIVIDE(adventure_time_min, project_session_time_min), 100)[OFFSET(50)] AS median_hopa_time_share_by_session,
  AVG(SAFE_DIVIDE(adventure_time_min, project_session_time_min)) AS avg_hopa_time_share_by_session,
  COUNTIF(SAFE_DIVIDE(adventure_time_min, project_session_time_min) IS NOT NULL) AS hopa_time_share_by_session_n,
  -- Shop share = shop time / ctx (NULL until Shop events arrive in version 4.1.100).
  APPROX_QUANTILES(shop_share_ctx, 100)[OFFSET(50)] AS median_shop_time_share,
  AVG(shop_share_ctx) AS avg_shop_time_share,
  COUNTIF(shop_share_ctx IS NOT NULL) AS shop_time_share_n,
  -- Subtracted HOPA share: (ctx - META - Shop) / ctx (B) — same denominator as META and Shop
  -- above, so the three add up to 1 per player-day.
  APPROX_QUANTILES(hopa_share_ctx, 100)[OFFSET(50)] AS median_hopa_time_share,
  AVG(hopa_share_ctx) AS avg_hopa_time_share,
  COUNTIF(hopa_share_ctx IS NOT NULL) AS hopa_time_share_n,
  APPROX_QUANTILES(career_level, 100)[OFFSET(50)] AS median_career_level,
  AVG(career_level) AS avg_career_level,
  COUNTIF(career_level IS NOT NULL) AS career_level_n,
FROM \`${tableFullPath}\` ds
${join}
${where}
${maxDayNoNum != null ? `AND active_day_no <= ${maxDayNoNum}` : ''}
GROUP BY ${splitGroupBy}active_day_no
ORDER BY ${splitGroupBy}active_day_no ASC
`;

                // INSTALL-DAY mode: install_day_no has gaps (player_days has rows for
                // ACTIVE days only). Densify retention-style (cf.
                // common/retention/data-loaders/retention-by-day-no.ts): each active
                // day's running counts carry forward until the day before the next
                // active day, and the last active day carries forward to the player's
                // maturity (max_day_no), so inactive days are included and the share
                // dilutes over the tail. Activity share (meta_share, etc.) is presence-
                // based, so it is NOT densified: it stays an aggregate over active rows.
                const installSegEnd =
                    maxDayNoNum != null
                        ? `LEAST(seg_end, ${maxDayNoNum})`
                        : 'seg_end';
                const installMaxDayNoWhere =
                    maxDayNoNum != null
                        ? `WHERE install_day_no <= ${maxDayNoNum}`
                        : '';
                const installSplitJoinOn = splitGroupBy
                    ? 'AND a.split_group = d.split_group'
                    : '';
                const installQuery = `
-- DataLoaderId: ${this.instanceId}

WITH source AS (
  SELECT
    ${splitSelect}player_id,
    install_day_no,
    active_day_no,
    running_hopa_days_count AS rh,
    running_meta_days_count AS rm,
    running_meta_prod_days_count AS rmp,
    running_core_loop_days_count AS rc,
    inspiration_gain_daily AS igd,
    meta_share_ctx,
    hopa_share_ctx,
    shop_share_ctx,
    project_session_time_min,
    adventure_time_min,
    SAFE_DIVIDE(adventure_time_min, project_session_time_min) AS hopa_time_share_by_session,
    career_level,
    meta_user,
    hopa_user,
    core_loop_user,
    meta_prod_user,
    player_dim.revenue_sessions.maturity.max_day_no AS max_day_no
  FROM \`${tableFullPath}\` ds
  ${join}
  ${where}
)

-- presence-based activity share over ACTIVE install-day rows (no densification)
, active_agg AS (
  SELECT
    ${splitGroupBy}install_day_no AS day_no,
    SAFE_DIVIDE(COUNT(DISTINCT IF(meta_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS meta_share,
    SAFE_DIVIDE(COUNT(DISTINCT IF(hopa_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS hopa_share,
    SAFE_DIVIDE(COUNT(DISTINCT IF(core_loop_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS core_loop_share,
    SAFE_DIVIDE(COUNT(DISTINCT IF(meta_prod_user, player_id, NULL)), COUNT(DISTINCT player_id)) AS meta_productive_share,
    -- per-active-user Lifetime Days Share (UBI-1145): aggregated over the ACTIVE install-day rows
    -- only (NOT densified). share = running_<pop>_days_count / (install_day_no + 1); ALL = active
    -- days so far / calendar days = (active_day_no + 1) / (install_day_no + 1). This supersedes the
    -- per-cohort densified version (kept commented out below). lifetime_days_share_n is the active
    -- player count backing the aggregate (same population across all populations — running counts
    -- are non-null).
    APPROX_QUANTILES(SAFE_DIVIDE(active_day_no + 1, install_day_no + 1), 100)[OFFSET(50)] AS median_all_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rm, install_day_no + 1), 100)[OFFSET(50)] AS median_meta_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rmp, install_day_no + 1), 100)[OFFSET(50)] AS median_meta_prod_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rh, install_day_no + 1), 100)[OFFSET(50)] AS median_hopa_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rc, install_day_no + 1), 100)[OFFSET(50)] AS median_core_loop_lifetime_days_share,
    AVG(SAFE_DIVIDE(active_day_no + 1, install_day_no + 1)) AS avg_all_lifetime_days_share,
    AVG(SAFE_DIVIDE(rm, install_day_no + 1)) AS avg_meta_lifetime_days_share,
    AVG(SAFE_DIVIDE(rmp, install_day_no + 1)) AS avg_meta_prod_lifetime_days_share,
    AVG(SAFE_DIVIDE(rh, install_day_no + 1)) AS avg_hopa_lifetime_days_share,
    AVG(SAFE_DIVIDE(rc, install_day_no + 1)) AS avg_core_loop_lifetime_days_share,
    COUNT(DISTINCT player_id) AS lifetime_days_share_n,
    -- daily inspiration gain per active user: daily (non-cumulative) value, so aggregated over the
    -- ACTIVE install-day rows like the activity shares (NOT densified).
    APPROX_QUANTILES(igd, 100)[OFFSET(50)] AS median_daily_inspiration_gain,
    AVG(igd) AS avg_daily_inspiration_gain,
    -- per-active-user time share (denominator = ctx) and career level; RAW/uncapped (may exceed 1),
    -- aggregated over the ACTIVE install-day rows (NOT densified); NULLs excluded by APPROX_QUANTILES/AVG.
    APPROX_QUANTILES(meta_share_ctx, 100)[OFFSET(50)] AS median_meta_time_share,
    AVG(meta_share_ctx) AS avg_meta_time_share,
    COUNTIF(meta_share_ctx IS NOT NULL) AS meta_time_share_n,
    -- Event-derived HOPA share: adventure time over engaged project-session time (C).
    APPROX_QUANTILES(hopa_time_share_by_session, 100)[OFFSET(50)] AS median_hopa_time_share_by_session,
    AVG(hopa_time_share_by_session) AS avg_hopa_time_share_by_session,
    COUNTIF(hopa_time_share_by_session IS NOT NULL) AS hopa_time_share_by_session_n,
    -- Shop is read from the source even while all values are NULL. NULLs are excluded
    -- from the aggregates, so n = 0 until data arrives.
    APPROX_QUANTILES(shop_share_ctx, 100)[OFFSET(50)] AS median_shop_time_share,
    AVG(shop_share_ctx) AS avg_shop_time_share,
    COUNTIF(shop_share_ctx IS NOT NULL) AS shop_time_share_n,
    -- Subtracted HOPA share: (ctx - META - Shop) / ctx (B) — same denominator as META and Shop
    -- above, so the three add up to 1 per player-day.
    APPROX_QUANTILES(hopa_share_ctx, 100)[OFFSET(50)] AS median_hopa_time_share,
    AVG(hopa_share_ctx) AS avg_hopa_time_share,
    COUNTIF(hopa_share_ctx IS NOT NULL) AS hopa_time_share_n,
    APPROX_QUANTILES(career_level, 100)[OFFSET(50)] AS median_career_level,
    AVG(career_level) AS avg_career_level,
    COUNTIF(career_level IS NOT NULL) AS career_level_n
  FROM source
  ${installMaxDayNoWhere}
  GROUP BY ${splitGroupBy}day_no
)

/* PER-COHORT (densified) Lifetime Days Share — superseded by the per-active-user aggregation
   in active_agg above (UBI-1145). Kept for future re-enable: to restore, uncomment this block
   (segments + densified + the FULL OUTER JOIN final SELECT) and delete the per-active-user
   final SELECT that follows.

-- each active day's running counts hold from its install_day_no until the day before
-- the next active day; the last active day holds through to the player's maturity.
, segments AS (
  SELECT
    ${splitGroupBy}active_day_no, rh, rm, rmp, rc,
    install_day_no AS seg_start,
    COALESCE(
      LEAD(install_day_no) OVER (PARTITION BY player_id ORDER BY install_day_no) - 1,
      GREATEST(COALESCE(max_day_no, install_day_no), install_day_no)
    ) AS seg_end
  FROM source
)

-- densify each segment into one row per calendar install day, then take the per-day
-- median / mean of running_<pop>_days_count / (install_day_no + 1).
, densified AS (
  SELECT
    ${splitGroupBy}day_no,
    -- ALL = share of calendar days the player has been active = (active days so far)/(day_no+1).
    -- It is the ceiling envelope: every population is a subset of active days, so its share <= ALL's.
    APPROX_QUANTILES(SAFE_DIVIDE(active_day_no + 1, day_no + 1), 100)[OFFSET(50)] AS median_all_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rm, day_no + 1), 100)[OFFSET(50)] AS median_meta_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rmp, day_no + 1), 100)[OFFSET(50)] AS median_meta_prod_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rh, day_no + 1), 100)[OFFSET(50)] AS median_hopa_lifetime_days_share,
    APPROX_QUANTILES(SAFE_DIVIDE(rc, day_no + 1), 100)[OFFSET(50)] AS median_core_loop_lifetime_days_share,
    AVG(SAFE_DIVIDE(active_day_no + 1, day_no + 1)) AS avg_all_lifetime_days_share,
    AVG(SAFE_DIVIDE(rm, day_no + 1)) AS avg_meta_lifetime_days_share,
    AVG(SAFE_DIVIDE(rmp, day_no + 1)) AS avg_meta_prod_lifetime_days_share,
    AVG(SAFE_DIVIDE(rh, day_no + 1)) AS avg_hopa_lifetime_days_share,
    AVG(SAFE_DIVIDE(rc, day_no + 1)) AS avg_core_loop_lifetime_days_share
  FROM segments, UNNEST(GENERATE_ARRAY(seg_start, ${installSegEnd})) AS day_no
  GROUP BY ${splitGroupBy}day_no
)

SELECT
  ${splitGroupBy ? 'COALESCE(a.split_group, d.split_group) AS split_group,' : ''}
  COALESCE(a.day_no, d.day_no) AS day_no,
  1.0 AS all_share,
  a.meta_share,
  a.hopa_share,
  a.core_loop_share,
  a.meta_productive_share,
  d.median_all_lifetime_days_share,
  d.median_meta_lifetime_days_share,
  d.median_meta_prod_lifetime_days_share,
  d.median_hopa_lifetime_days_share,
  d.median_core_loop_lifetime_days_share,
  d.avg_all_lifetime_days_share,
  d.avg_meta_lifetime_days_share,
  d.avg_meta_prod_lifetime_days_share,
  d.avg_hopa_lifetime_days_share,
  d.avg_core_loop_lifetime_days_share,
  a.median_daily_inspiration_gain,
  a.avg_daily_inspiration_gain,
  a.median_meta_time_share,
  a.avg_meta_time_share,
  a.meta_time_share_n,
  a.median_career_level,
  a.avg_career_level,
  a.career_level_n
FROM active_agg a
FULL OUTER JOIN densified d
  ON a.day_no = d.day_no ${installSplitJoinOn}
ORDER BY day_no ASC
*/

-- PER-ACTIVE-USER final SELECT (UBI-1145): lifetime-share columns come from active_agg (a),
-- not the densified per-cohort CTE. Aggregated over active install-day rows only.
SELECT
  ${splitGroupBy ? 'a.split_group,' : ''}
  a.day_no,
  1.0 AS all_share,
  a.meta_share,
  a.hopa_share,
  a.core_loop_share,
  a.meta_productive_share,
  a.median_all_lifetime_days_share,
  a.median_meta_lifetime_days_share,
  a.median_meta_prod_lifetime_days_share,
  a.median_hopa_lifetime_days_share,
  a.median_core_loop_lifetime_days_share,
  a.avg_all_lifetime_days_share,
  a.avg_meta_lifetime_days_share,
  a.avg_meta_prod_lifetime_days_share,
  a.avg_hopa_lifetime_days_share,
  a.avg_core_loop_lifetime_days_share,
  a.lifetime_days_share_n,
  a.median_daily_inspiration_gain,
  a.avg_daily_inspiration_gain,
  a.median_meta_time_share,
  a.avg_meta_time_share,
  a.meta_time_share_n,
  a.median_shop_time_share,
  a.avg_shop_time_share,
  a.shop_time_share_n,
  a.median_hopa_time_share,
  a.avg_hopa_time_share,
  a.hopa_time_share_n,
  a.median_hopa_time_share_by_session,
  a.avg_hopa_time_share_by_session,
  a.hopa_time_share_by_session_n,
  a.median_career_level,
  a.avg_career_level,
  a.career_level_n
FROM active_agg a
ORDER BY day_no ASC
`;

                const query = isInstallDay ? installQuery : activeQuery;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
